/**
 * SPEC-TrainingSimulator §4.1 — key-editor extensions to the answer-key
 * entry shape, as PURE helpers (no React, no DOM) so the key editor, the
 * Phase B runner and the unit tests share one definition. The shapes
 * themselves now live in `shared/simulator-helpers.ts` (unfrozen in Phase
 * B) and are re-exported here; the editor-only helpers (normalisation,
 * choice-feedback fields, caps, save-error pointers) stay client-side.
 *
 *   fieldRationale — feedback per WRONG choice: `[gradeField][choice]`.
 *   boxes          — photo bounding boxes `{ target: "photo:N", x, y, w, h }`.
 *   highlights     — anchored card text `{ field, text, occurrence }`.
 *   valueHighlight — the same anchor, captured with the answer itself.
 *
 * Runner rules (spec §4.1 / §8.3): feedback for a wrong field resolves
 * fieldRationale[field][choice] → entry.rationale → question default; for
 * set-valued fields (`where`, `differs`) the choice is each selected value
 * that is not in the key, first in selection order (`resolveFeedback` in
 * shared/simulator-helpers.ts, run server-side). Boxes, highlights and the
 * value highlight are never sent before that question is submitted.
 */

import {
  Q10_ROWS, DIFFERS_VALUES, UNCLEAR_REASONS, Q7_UNCLEAR_REASONS, Q7_UNCLEAR_LABELS, HIGHLIGHT_FIELDS, questionApplies, isNA, differsOptionsFor, Q2_IN_POD_NA_LABEL, normalizeReferenceEntry,
  type AnswerKey, type QuestionId, type PhotoBox, type TextHighlight, type HighlightField, type FieldRationale,
} from "@shared/simulator-helpers";

// ─── Shapes ──────────────────────────────────────────────────────────────────
// The shapes now live in shared/simulator-helpers.ts (Phase B unfroze it so
// the server can type the reveal); re-exported here so existing imports hold.

export { HIGHLIGHT_FIELDS };
export type { PhotoBox, TextHighlight, HighlightField, FieldRationale };

/** Client-side caps — mirror the zod limits in server/routes/simulator.ts so the 51st box fails here, not at save. */
export const MAX_BOXES = 50;
export const MAX_HIGHLIGHTS = 20;
export const MAX_OCCURRENCE = 50;

export const isPhotoTarget = (t: unknown): t is string => typeof t === "string" && /^photo:[1-9]\d*$/.test(t);
export const photoIndexOf = (t: string): number | null => (isPhotoTarget(t) ? Number(t.slice(6)) : null);
export function targetLabel(t: string): string {
  const n = photoIndexOf(t);
  return n ? `Photo ${n}` : t;
}

// ─── Reading a key ───────────────────────────────────────────────────────────

export function boxesOf(key: AnswerKey, id: QuestionId): PhotoBox[] {
  const b = (key[id] as any)?.boxes;
  return Array.isArray(b) ? b : [];
}

/** Explicit highlights plus the answer's own anchor (first), which is outlined too. */
export function highlightsOf(key: AnswerKey, id: QuestionId): TextHighlight[] {
  const e = (key[id] as any) ?? {};
  const h: TextHighlight[] = Array.isArray(e.highlights) ? e.highlights : [];
  return e.valueHighlight && typeof e.valueHighlight.text === "string" ? [e.valueHighlight, ...h] : h;
}

/**
 * Boxes keyed before 2026-09-09 16:00 were stored as `{ photo: N, … }`; the
 * shape is now `{ target: "photo:N", … }`. Normalise on load and on save so
 * an older key renders and re-saves cleanly. Anything that is not a photo
 * box (e.g. a leftover `card:*` target from the retired card-box iteration)
 * is dropped rather than sent to a validator that will reject it.
 */
export function normalizeKeyBoxes(key: AnswerKey): AnswerKey {
  let changedAny = false;
  const out: Record<string, unknown> = { ...key };
  for (const [qid, entry] of Object.entries(key)) {
    const e = entry as any;
    if (!e || !Array.isArray(e.boxes)) continue;
    let changed = false;
    const boxes = e.boxes
      .map((b: any) => {
        if (b && isPhotoTarget(b.target)) return b;
        changed = true;
        if (b && typeof b.photo === "number" && b.photo >= 1) return { target: `photo:${b.photo}`, x: b.x, y: b.y, w: b.w, h: b.h };
        return null;
      })
      .filter(Boolean);
    if (changed) { out[qid] = { ...e, boxes }; changedAny = true; }
  }
  return changedAny ? (out as AnswerKey) : key;
}

/** Drops empty strings so the saved key only carries feedback that was written. */
export function pruneFieldRationale(fr: FieldRationale): FieldRationale | undefined {
  const out: FieldRationale = {};
  for (const [field, byChoice] of Object.entries(fr)) {
    const kept: Record<string, string> = {};
    for (const [choice, text] of Object.entries(byChoice ?? {})) if (typeof text === "string" && text.trim()) kept[choice] = text;
    if (Object.keys(kept).length) out[field] = kept;
  }
  return Object.keys(out).length ? out : undefined;
}

// ─── Per-choice feedback fields ──────────────────────────────────────────────

export interface ChoiceOption { value: string; label: string }
export interface ChoiceFeedbackField { field: string; label: string; options: ChoiceOption[] }

const yesNo = (opts: Record<string, string>): ChoiceOption[] => [
  { value: "yes", label: opts.yes ?? "Yes" },
  { value: "no", label: opts.no ?? "No" },
];
const yesNoUnclear = (opts: Record<string, string>): ChoiceOption[] => [...yesNo(opts), { value: "unclear", label: opts.unclear ?? "Unclear" }];
const unclearReasons = (opts: Record<string, string>): ChoiceOption[] =>
  UNCLEAR_REASONS.map((r) => ({ value: r, label: opts[r] ?? (r === "pod_missing" ? "POD missing" : "Can't interpret POD") }));

/**
 * Which wrong choices can be given their own feedback for a question, given
 * the keyed entry. Only enumerable fields on the keyed branch are listed —
 * a choice off the keyed branch is graded at the parent pill, so it gets
 * the parent's feedback. Options equal to the keyed value are skipped, and
 * nothing is listed until the field is keyed. Field names are the ones
 * `gradeAnswer` emits (`inPod`, `where`, `rows.street`, …) so the runner can
 * look feedback up straight from the grade.
 */
export function choiceFeedbackFields(
  id: QuestionId,
  entry: unknown,
  evidence: ChoiceOption[],
  labels: Record<string, string> = {},
  options: Record<string, string> = {},
  /** The whole key: which q6 differs choices exist depends on q2 (no Unit when N/A). */
  key?: AnswerKey | null,
): ChoiceFeedbackField[] {
  const e = (id === "q6_reference" ? normalizeReferenceEntry(entry as any) ?? {} : (entry ?? {})) as any;
  const out: ChoiceFeedbackField[] = [];
  const not = (opts: ChoiceOption[], keyed: unknown) => (keyed ? opts.filter((o) => o.value !== keyed) : []);
  const notIn = (opts: ChoiceOption[], keyed: unknown) => {
    if (!Array.isArray(keyed)) return [];
    const set = new Set(keyed);
    return opts.filter((o) => !set.has(o.value));
  };
  const push = (field: string, label: string, opts: ChoiceOption[]) => { if (opts.length) out.push({ field, label, options: opts }); };
  const whereLabel = labels.where ?? "Where do you see it?";
  const keyedValue = typeof e.value === "string" ? e.value.trim() : "";
  switch (id) {
    case "q1_street":
    case "q2_unit": {
      // q2's N/A pill is the one enumerable choice on an otherwise free-text
      // value: listed once the unit is keyed to something other than N/A.
      if (id === "q2_unit" && keyedValue && !isNA(keyedValue)) {
        push("value", "Expected unit", [{ value: "N/A", label: labels.na ?? "N/A" }]);
      }
      // A key with no unit asks nothing about the POD, so there are no
      // `inPod`/`where` choices for the trainee to get wrong.
      if (id === "q2_unit" && isNA(keyedValue)) break;
      push("inPod", labels.inPod ?? "Is it visible in the POD?", not(id === "q2_unit" ? [...yesNoUnclear(options), { value: "na", label: options.inPodNa ?? Q2_IN_POD_NA_LABEL }] : yesNoUnclear(options), e.inPod));
      if (e.inPod === "no") push("otherSeen", labels.otherSeen ?? "Do you see a different value?", not(yesNo(options), e.otherSeen));
      if (e.inPod === "unclear") push("unclearReason", labels.unclearReason ?? "Why is it unclear?", not(unclearReasons(options), e.unclearReason));
      if (e.inPod === "yes" || e.inPod === "unclear" || (e.inPod === "no" && e.otherSeen === "yes")) push("where", whereLabel, notIn(evidence, e.where));
      break;
    }
    case "q4_actual_dz":
      push("where", labels.where ?? "Which photos show it?", notIn(evidence, e.where));
      break;
    case "q5_meets":
      push("value", "Meets or exceeds?", not(yesNo(options), e.value));
      if (e.value === "no") push("clearWhy", labels.clearWhy ?? "Is it clear why the driver deviated?", not(yesNo(options), e.clearWhy));
      break;
    case "q6_reference":
      push("value", "Has reference photos?", not(yesNo(options), e.value));
      if (e.value === "yes") push("conflict", labels.conflict ?? "Past photos show a conflict?", not(yesNo(options), e.conflict));
      if (e.value === "yes" && e.conflict === "yes") push("differs", labels.differs ?? "Which looks wrong?", notIn(differsOptionsFor(key).map((d) => ({ value: d, label: options[d] ?? d })), e.differs));
      break;
    case "q7_return_note":
      push("value", "Said returns collected?", not(yesNoUnclear(options), e.value));
      if (e.value === "unclear") push("unclearReason", labels.unclearReason ?? "Why is it unclear?", not(Q7_UNCLEAR_REASONS.map((r) => ({ value: r, label: options[r] ?? Q7_UNCLEAR_LABELS[r] })), e.unclearReason));
      break;
    case "q8_missed_return":
      push("value", "Missed return?", not(yesNo(options), e.value));
      if (e.value === "yes") push("where", labels.where ?? "Which photos?", notIn(evidence, e.where));
      break;
    case "q9_condition":
      push("value", "Good condition?", not(yesNo(options), e.value));
      break;
    case "q10_decision": {
      const rows = e.rows ?? {};
      const rowOpts: ChoiceOption[] = [{ value: "yes", label: options.yes ?? "Yes" }, { value: "no", label: options.no ?? "No" }, { value: "unsure", label: options.unsure ?? "Unsure" }];
      const rowLabel: Record<string, string> = { street: labels.street ?? "Street number correct?", unit: labels.unit ?? "Unit correct?", dz: labels.dz ?? "Drop zone OK?", returns: labels.returns ?? "Returns OK?" };
      const unitRowOpts: ChoiceOption[] = [...rowOpts, { value: "na", label: options.na ?? "N/A" }];
      for (const r of Q10_ROWS) if (rows[r]) push(`rows.${r}`, rowLabel[r], not(r === "unit" ? unitRowOpts : rowOpts, rows[r]));
      if (e.decision) push("decision", "Decision", not([{ value: "GOOD", label: "Good" }, { value: "BAD", label: "Bad" }], e.decision));
      break;
    }
  }
  return out;
}

/** Turn a zod issue path (`["answerKey","q1_street","boxes",0,"target"]`) into a readable pointer for the save toast. */
export function describeKeyIssue(issue: { path?: Array<string | number>; message?: string } | undefined, questionSort?: (id: string) => number | undefined): string | null {
  if (!issue || !Array.isArray(issue.path)) return null;
  const path = issue.path.filter((p) => p !== "answerKey");
  const qid = typeof path[0] === "string" ? path[0] : null;
  const sort = qid && questionSort ? questionSort(qid) : undefined;
  const where = qid ? (sort ? `Q${sort}` : qid) : "key";
  const rest = path.slice(1).map((p) => (typeof p === "number" ? `[${p}]` : `.${p}`)).join("").replace(/^\./, "");
  return `${where}${rest ? ` → ${rest}` : ""}${issue.message ? `: ${issue.message}` : ""}`;
}

// ─── Per-choice feedback shared across a delivery ────────────────────────────

/**
 * Choice-feedback fields whose choices mean the same thing on every question
 * that has them — the evidence sources (`where`), and the q1/q2 POD pills —
 * so a line written for one question is a sensible starting point for the
 * others on the same delivery. `value`, `clearWhy`, `conflict`, `differs`,
 * `rows.*` and `decision` are question-specific and never copied.
 */
export const SHARED_CHOICE_FIELDS: Record<string, readonly QuestionId[]> = {
  where: ["q1_street", "q2_unit", "q4_actual_dz", "q8_missed_return"],
  inPod: ["q1_street", "q2_unit"],
  otherSeen: ["q1_street", "q2_unit"],
  unclearReason: ["q1_street", "q2_unit", "q7_return_note"],
};

/** Whether `choice` is the KEYED (correct) value of `field` on this entry — feedback for it would never be shown. */
function isKeyedChoice(entry: any, field: string, choice: string): boolean {
  if (!entry || typeof entry !== "object") return false;
  if (field === "where") return Array.isArray(entry.where) && entry.where.includes(choice);
  return entry[field] === choice;
}

/** Identity of one feedback line, for the editor's session-local set of copies it made. */
export const feedbackLineId = (qid: QuestionId, field: string, choice: string) => `${qid}|${field}|${choice}`;

/**
 * Sarah, 2026-09-11: the same "why this choice is wrong" line was being typed
 * on every question of a delivery. When a line for a SHARED field/choice is
 * committed on one question, copy it to the other questions of the item that
 * share that field — but only where the line is still blank, or is a copy
 * THIS editing session made (`mirrors`, kept by the key form), so a fixed
 * typo reaches every copy. A line that was saved earlier or typed by hand is
 * never overwritten, even when it happens to read the same (audit W1: two
 * identical hand-written lines must stay independent). Nothing is copied onto
 * a question the key does not ask (W4) or where the choice is the keyed answer.
 *
 * Returns the same `key` object when nothing changed, plus the lines written.
 */
export function propagateChoiceFeedback(
  key: AnswerKey,
  sourceId: QuestionId,
  field: string,
  choice: string,
  text: string,
  mirrors: ReadonlySet<string> = new Set(),
): { key: AnswerKey; written: QuestionId[] } {
  const targets = SHARED_CHOICE_FIELDS[field];
  if (!targets || !targets.includes(sourceId)) return { key, written: [] };
  const next: Record<string, unknown> = { ...key };
  const written: QuestionId[] = [];
  for (const qid of targets) {
    if (qid === sourceId || !questionApplies(qid, key)) continue;
    const entry: any = (key as any)[qid] ?? {};
    if (isKeyedChoice(entry, field, choice)) continue;
    const current: string = entry.fieldRationale?.[field]?.[choice] ?? "";
    const mirror = current.trim() === "" || mirrors.has(feedbackLineId(qid, field, choice));
    if (!mirror || current === text) continue;
    const fr = pruneFieldRationale({ ...(entry.fieldRationale ?? {}), [field]: { ...(entry.fieldRationale?.[field] ?? {}), [choice]: text } });
    const e: any = { ...entry, fieldRationale: fr };
    if (!fr) delete e.fieldRationale;
    next[qid] = e;
    written.push(qid);
  }
  return written.length ? { key: next as AnswerKey, written } : { key, written };
}
